import { size } from '@floating-ui/dom';
import { useLingui } from '@lingui/react/macro';
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  EVENTS,
  type EventData,
  Joyride,
  STATUS,
  type Step,
} from 'react-joyride';
import { useHasAccessToken } from '@/auth/api';
import { Gate, useGate } from '@/gate';
import { SpotlightTooltip } from './SpotlightTooltip';
import { clearSpotlightSeen, isSpotlightSeen, markSpotlightSeen } from './seen';
import type { SpotlightStep } from './types';

type Registration = {
  id: string;
  steps: SpotlightStep[];
};

type ActiveTour = {
  ids: string[];
  steps: SpotlightStep[];
};

type SpotlightContextValue = {
  /** Register a tour and auto-start it if unseen and nothing else is running. */
  register: (registration: Registration) => void;
  /** Drop a tour's registration (called on component unmount). */
  unregister: (id: string) => void;
  /** Clear the "seen" flag and run the tour again. */
  replay: (id: string) => void;
  /** Register a `<Spotlight id>` target element. */
  registerTarget: (id: string, el: HTMLElement) => void;
  /** Drop a `<Spotlight id>` target element. */
  unregisterTarget: (id: string, el: HTMLElement) => void;
};

const SpotlightContext = createContext<SpotlightContextValue | null>(null);

export const useSpotlightContext = () => {
  const ctx = useContext(SpotlightContext);
  if (!ctx) throw new Error('SpotlightProvider missing');
  return ctx;
};

const isVisible = (el: HTMLElement) =>
  el.offsetParent !== null || getComputedStyle(el).position === 'fixed';

export const SpotlightProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { t } = useLingui();
  const hasAccessToken = useHasAccessToken();
  const hasAccessTokenRef = useRef(hasAccessToken);
  hasAccessTokenRef.current = hasAccessToken;
  const gate = useGate();
  const isDisabled = !gate([Gate.hooks.isIdentified(2)]);
  const isDisabledRef = useRef(isDisabled);
  isDisabledRef.current = isDisabled;
  const registry = useRef(new Map<string, SpotlightStep[]>());
  const pending = useRef(new Set<string>());
  const targets = useRef(new Map<string, Set<HTMLElement>>());
  const mergeBuffer = useRef(new Map<string, SpotlightStep[]>());
  const flushScheduled = useRef(false);
  const [active, setActive] = useState<ActiveTour | null>(null);

  const resolveTarget = useCallback((id: string): HTMLElement | null => {
    const set = targets.current.get(id);
    if (!set) return null;
    for (const el of set) if (isVisible(el)) return el;
    return null;
  }, []);

  const filterReachableSteps = useCallback(
    (steps: SpotlightStep[]) =>
      steps.filter((step) => resolveTarget(step.id) !== null),
    [resolveTarget],
  );

  const toJoyrideStep = useCallback(
    (step: SpotlightStep): Step => {
      const el = resolveTarget(step.id);
      return {
        target: el ?? `[data-spotlight-id="${step.id}"]`,
        content: step.description,
        title: step.title,
        ...(step.placement ? { placement: step.placement } : {}),
      };
    },
    [resolveTarget],
  );

  /**
   * Drains the merge buffer into a single tour. Registrations that land in
   * the same tick (e.g., a layout and its page both mounting on the same
   * commit) merge into one combined tour instead of running sequentially.
   */
  const flushMergeBuffer = useCallback(() => {
    flushScheduled.current = false;
    if (!hasAccessTokenRef.current) return;
    if (isDisabledRef.current) return;
    const buffer = mergeBuffer.current;
    if (buffer.size === 0) return;
    mergeBuffer.current = new Map();

    const ids: string[] = [];
    const steps: SpotlightStep[] = [];
    for (const [id, raw] of buffer) {
      if (isSpotlightSeen(id)) continue;
      const reachable = filterReachableSteps(raw);
      if (!reachable.length) {
        pending.current.add(id);
        continue;
      }
      ids.push(id);
      steps.push(...reachable);
    }

    if (!ids.length) return;

    setActive((current) => {
      if (current) {
        for (const id of ids) {
          if (!current.ids.includes(id)) pending.current.add(id);
        }
        return current;
      }
      for (const id of ids) pending.current.delete(id);
      return { ids, steps };
    });
  }, [filterReachableSteps]);

  const scheduleMergeFlush = useCallback(() => {
    if (flushScheduled.current) return;
    flushScheduled.current = true;
    queueMicrotask(flushMergeBuffer);
  }, [flushMergeBuffer]);

  const promoteNextPending = useCallback(() => {
    if (isDisabledRef.current) return;
    for (const id of Array.from(pending.current)) {
      if (isSpotlightSeen(id)) {
        pending.current.delete(id);
        continue;
      }
      const steps = registry.current.get(id);
      if (!steps?.length) continue;
      const reachable = filterReachableSteps(steps);
      if (!reachable.length) continue;
      pending.current.delete(id);
      setActive({ ids: [id], steps: reachable });
      return;
    }
  }, [filterReachableSteps]);

  const register = useCallback(
    (registration: Registration) => {
      registry.current.set(registration.id, registration.steps);
      // If the id is already part of the active tour, refresh its steps so
      // content changes (e.g., locale switch) flow through to Joyride.
      setActive((current) => {
        if (!current?.ids.includes(registration.id)) return current;
        const rebuilt: SpotlightStep[] = [];
        for (const id of current.ids) {
          const raw = registry.current.get(id);
          if (!raw) continue;
          rebuilt.push(...filterReachableSteps(raw));
        }
        return { ...current, steps: rebuilt };
      });
      if (isSpotlightSeen(registration.id)) return;
      mergeBuffer.current.set(registration.id, registration.steps);
      scheduleMergeFlush();
    },
    [filterReachableSteps, scheduleMergeFlush],
  );

  const unregister = useCallback((id: string) => {
    registry.current.delete(id);
    pending.current.delete(id);
    mergeBuffer.current.delete(id);
    // intentionally keep `active` running — the active tour holds its own
    // snapshot, so the consumer unmounting doesn't affect the in-flight tour
  }, []);

  const replay = useCallback(
    (id: string) => {
      const steps = registry.current.get(id);
      if (!steps?.length) return;
      clearSpotlightSeen(id);
      pending.current.delete(id);
      if (isDisabledRef.current) {
        // suppressed on this route — defer until the user navigates away
        pending.current.add(id);
        return;
      }
      const reachable = filterReachableSteps(steps);
      if (!reachable.length) {
        pending.current.add(id);
        return;
      }
      // replay always preempts whatever is running
      setActive({ ids: [id], steps: reachable });
    },
    [filterReachableSteps],
  );

  const registerTarget = useCallback(
    (id: string, el: HTMLElement) => {
      let set = targets.current.get(id);
      if (!set) {
        set = new Set();
        targets.current.set(id, set);
      }
      set.add(el);
      // a target appearing might unblock a queued tour
      if (pending.current.has(id)) {
        const steps = registry.current.get(id);
        if (steps) {
          mergeBuffer.current.set(id, steps);
          scheduleMergeFlush();
        }
      }
    },
    [scheduleMergeFlush],
  );

  const unregisterTarget = useCallback((id: string, el: HTMLElement) => {
    const set = targets.current.get(id);
    if (!set) return;
    set.delete(el);
    if (set.size === 0) targets.current.delete(id);
  }, []);

  useEffect(() => {
    if (hasAccessToken) {
      // drain anything that registered while logged out
      scheduleMergeFlush();
    } else {
      // logout: tear down any in-flight tour
      setActive(null);
    }
  }, [hasAccessToken, scheduleMergeFlush]);

  useEffect(() => {
    if (isDisabled) {
      // navigated into a route where spotlights are suppressed — drop any
      // in-flight tour and re-queue its ids so they run again on exit
      setActive((current) => {
        if (!current) return null;
        for (const id of current.ids) pending.current.add(id);
        return null;
      });
    } else {
      scheduleMergeFlush();
    }
  }, [isDisabled, scheduleMergeFlush]);

  const handleEvent = useCallback(
    (data: EventData) => {
      if (!active) return;
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        // target vanished mid-tour (route change, modal closed, etc.) — stop
        // without persisting "seen" so the user can encounter it again
        setActive(null);
        promoteNextPending();
        return;
      }
      const finished: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
      if (data.type === EVENTS.TOUR_END || finished.includes(data.status)) {
        for (const id of active.ids) markSpotlightSeen(id);
        setActive(null);
        promoteNextPending();
      }
    },
    [active, promoteNextPending],
  );

  const locale = useMemo(
    () => ({
      back: t`Back`,
      close: t`Close`,
      last: t`Done`,
      next: t`Next`,
      skip: t`Skip`,
    }),
    [t],
  );

  const value = useMemo<SpotlightContextValue>(
    () => ({ register, unregister, replay, registerTarget, unregisterTarget }),
    [register, unregister, replay, registerTarget, unregisterTarget],
  );

  return (
    <SpotlightContext.Provider value={value}>
      {children}
      {hasAccessToken && !isDisabled && active && (
        <Joyride
          steps={active.steps.map(toJoyrideStep)}
          run
          continuous
          scrollToFirstStep
          locale={locale}
          onEvent={handleEvent}
          tooltipComponent={SpotlightTooltip}
          floatingOptions={{
            shiftOptions: {
              padding: 16,
              mainAxis: true,
              crossAxis: true,
              rootBoundary: 'viewport',
              boundary: 'clippingAncestors',
            },
            flipOptions: {
              padding: 16,
              rootBoundary: 'viewport',
              boundary: 'clippingAncestors',
              fallbackStrategy: 'bestFit',
            },
            middleware: [
              size({
                padding: 16,
                rootBoundary: 'viewport',
                boundary: 'clippingAncestors',
                apply: ({ availableWidth, availableHeight, elements }) => {
                  Object.assign(elements.floating.style, {
                    maxWidth: `${Math.max(0, availableWidth)}px`,
                    maxHeight: `${Math.max(0, availableHeight)}px`,
                  });
                },
              }),
            ],
          }}
          styles={{
            floater: {
              filter: 'none',
            },
          }}
          options={{
            arrowColor: 'var(--color-surface-l0)',
            spotlightPadding: 1,
            overlayColor: 'rgba(0, 0, 0, 0.7)',
            arrowSpacing: 6,
            arrowBase: 20,
            arrowSize: 11,
            zIndex: 1000,
            showProgress: false,
            skipBeacon: true,
            buttons: ['back', 'skip', 'primary'],
            overlayClickAction: false,
            spotlightRadius: 12,
          }}
        />
      )}
    </SpotlightContext.Provider>
  );
};
